import React, { useState } from 'react';
import { Building2, ShoppingCart, Sparkles, Home, Coffee, Eye, ArrowRight } from 'lucide-react';

const BuildYourWebsite = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const templates = [
    {
      id: 1,
      title: 'Builder & Construction',
      description: 'Perfect for construction companies, architects, and building contractors. Showcase your projects with style.',
      image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=600&h=400&fit=crop',
      icon: Building2,
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b, #ea580c)',
    },
    {
      id: 2,
      title: 'Supermarket & Grocery',
      description: 'Ideal for supermarkets, grocery stores, and food retailers. Display products and deals attractively.',
      image: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=600&h=400&fit=crop',
      icon: ShoppingCart,
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981, #059669)',
    },
    {
      id: 3,
      title: 'Beauty Salon & Spa',
      description: 'Elegant design for beauty salons, spas, and wellness centers. Highlight your services beautifully.',
      image: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=600&h=400&fit=crop',
      icon: Sparkles,
      color: '#ec4899',
      gradient: 'linear-gradient(135deg, #ec4899, #db2777)',
    },
    {
      id: 4,
      title: 'Real Estate & Property',
      description: 'Professional template for real estate agents and property listings. Showcase properties effectively.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop',
      icon: Home,
      color: '#3b82f6',
      gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    },
    {
      id: 5,
      title: 'Restaurant & Cafe',
      description: 'Delicious design for restaurants, cafes, and food businesses. Make your menu stand out.',
      image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&h=400&fit=crop',
      icon: Coffee,
      color: '#8b5cf6',
      gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    }
  ];

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
      padding: '4rem 2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    header: {
      textAlign: 'center',
      marginBottom: '3rem',
      maxWidth: '800px',
      margin: '0 auto 4rem',
    },
    title: {
      fontSize: 'clamp(2.5rem, 5vw, 4rem)',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #1f2937, #111827)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      marginBottom: '1rem',
    },
    subtitle: {
      fontSize: '1.25rem',
      color: '#6b7280',
      lineHeight: 1.6,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      gap: '2rem',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    card: (isHovered, color) => ({
      background: 'white',
      borderRadius: '20px',
      overflow: 'hidden',
      boxShadow: isHovered 
        ? `0 20px 60px ${color}40` 
        : '0 4px 20px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      transform: isHovered ? 'translateY(-12px)' : 'translateY(0)',
      cursor: 'pointer',
      border: isHovered ? `2px solid ${color}` : '2px solid transparent',
    }),
    imageContainer: {
      position: 'relative',
      height: '240px',
      overflow: 'hidden',
    },
    image: (isHovered) => ({
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.4s ease',
      transform: isHovered ? 'scale(1.1)' : 'scale(1)',
    }),
    overlay: (gradient) => ({
      position: 'absolute',
      inset: 0,
      background: gradient,
      opacity: 0.1,
    }),
    iconBadge: (color) => ({
      position: 'absolute',
      top: '20px',
      right: '20px',
      background: 'white',
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: color,
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
    }),
    content: {
      padding: '2rem',
    },
    cardTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '0.75rem',
    },
    description: {
      fontSize: '1rem',
      color: '#6b7280',
      lineHeight: 1.6,
      marginBottom: '1.5rem',
    },
    buttonGroup: {
      display: 'flex',
      gap: '1rem',
    },
    primaryButton: (gradient, isHovered) => ({
      flex: 1,
      background: gradient,
      color: 'white',
      padding: '0.875rem 1.5rem',
      borderRadius: '12px',
      border: 'none',
      fontWeight: '600',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      transform: isHovered ? 'scale(1.05)' : 'scale(1)',
      boxShadow: isHovered ? '0 8px 20px rgba(0, 0, 0, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
    }),
    secondaryButton: (color) => ({
      flex: 1,
      background: 'transparent',
      color: color,
      padding: '0.875rem 1.5rem',
      borderRadius: '12px',
      border: `2px solid ${color}`,
      fontWeight: '600',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
    }),
    badge: (color) => ({
      display: 'inline-block',
      background: `${color}15`,
      color: color,
      padding: '0.5rem 1rem',
      borderRadius: '20px',
      fontSize: '0.875rem',
      fontWeight: '600',
      marginBottom: '1rem',
    }),
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .template-card {
            animation: fadeIn 0.6s ease forwards;
          }
          .template-card:nth-child(1) { animation-delay: 0.1s; }
          .template-card:nth-child(2) { animation-delay: 0.2s; }
          .template-card:nth-child(3) { animation-delay: 0.3s; }
          .template-card:nth-child(4) { animation-delay: 0.4s; }
          .template-card:nth-child(5) { animation-delay: 0.5s; }
          .secondary-button:hover {
            background: currentColor;
            color: white !important;
          }
        `}
      </style>

      <div style={styles.header}>
        <h1 style={styles.title}>Choose Your Perfect Template</h1>
        <p style={styles.subtitle}>
          Select from our professionally designed templates tailored for different industries. 
          Each template is fully customizable and optimized for your business needs.
        </p>
      </div>

      <div style={styles.grid}>
        {templates.map((template, index) => {
          const Icon = template.icon;
          const isHovered = hoveredCard === template.id;
          
          return (
            <div
              key={template.id}
              className="template-card"
              style={styles.card(isHovered, template.color)}
              onMouseEnter={() => setHoveredCard(template.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div style={styles.imageContainer}>
                <img
                  src={template.image}
                  alt={template.title}
                  style={styles.image(isHovered)}
                />
                <div style={styles.overlay(template.gradient)}></div>
                <div style={styles.iconBadge(template.color)}>
                  <Icon size={24} />
                </div>
              </div>
              
              <div style={styles.content}>
                <div style={styles.badge(template.color)}>
                  Template {template.id}
                </div>
                <h3 style={styles.cardTitle}>{template.title}</h3>
                <p style={styles.description}>{template.description}</p>
                
                <div style={styles.buttonGroup}>
                  <a
                    href={`/builder/${template.id}`}
                    style={styles.primaryButton(template.gradient, isHovered)}
                  >
                    Use Template
                    <ArrowRight size={18} />
                  </a>
                  <button
                    style={styles.secondaryButton(template.color)}
                    className="secondary-button"
                    onClick={() => alert(`Preview Template ${template.id}`)}
                  >
                    <Eye size={18} />
                    Preview
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BuildYourWebsite;